import {
  type FloatingToolbarState,
  flip,
  offset,
  useFloatingToolbar,
  useFloatingToolbarState,
} from '@platejs/floating'
import { useComposedRef } from '@udecode/cn'
import { KEYS } from 'platejs'
import { useEditorId, useEventEditorValue, usePluginOption } from 'platejs/react'
import * as React from 'react'

import { Toolbar, cn } from '@hap/components'

export const FloatingToolbar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Toolbar> & {
    state?: FloatingToolbarState
  }
>(({ children, className, state, ...props }, ref) => {
  const editorId = useEditorId()
  const focusedEditorId = useEventEditorValue('focus')
  const isFloatingLinkOpen = !!usePluginOption({ key: KEYS.link }, 'mode')
  const isAIChatOpen = usePluginOption({ key: KEYS.aiChat }, 'open')

  const floatingToolbarState = useFloatingToolbarState({
    editorId,
    focusedEditorId,
    hideToolbar: isFloatingLinkOpen || isAIChatOpen,
    ...state,
    floatingOptions: {
      middleware: [
        offset(12),
        flip({
          fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
          padding: 12,
        }),
      ],
      placement: 'top',
      ...state?.floatingOptions,
    },
  })

  const {
    clickOutsideRef,
    hidden,
    props: rootProps,
    ref: floatingRef,
  } = useFloatingToolbar(floatingToolbarState)

  const composedRef = useComposedRef<HTMLDivElement>(ref, floatingRef)

  if (hidden) return null

  return (
    <div ref={clickOutsideRef}>
      <Toolbar
        {...props}
        {...rootProps}
        ref={composedRef}
        className={cn(
          'scrollbar-hide bg-popover absolute z-50 overflow-x-auto whitespace-nowrap rounded-md border p-1 opacity-100 shadow-md print:hidden',
          'max-w-[80vw]',
          className,
        )}
      >
        {children}
      </Toolbar>
    </div>
  )
})
